import {createSelector} from '@reduxjs/toolkit';
import isEqual from 'react-fast-compare';
import {Forecast} from '../../utils';
import {selectIsPremium} from '../billing/selectors';
import {deriveForecastSelection} from '../forecastModel/deriveForecastSelection';
import {resolveSelectedForecastModel} from '../forecastModel/resolveSelectedForecastModel';
import {CommonRootState} from '../types';
import type {NormalizedForecastModel} from '../../utils/Forecast/types';
import {USER_LOCATION_HASH} from '../../utils/MyWeather/constants';
import {
  selectForecastModelMode,
  selectForecastModelOrder,
  selectForecastModelPins,
  selectForecastModelStrategy,
} from '../userPreference/selectors';

export const getUserLocation = (state: CommonRootState) =>
  state.location.userLocation;

export const getRawSelectedLocationHash = (state: CommonRootState) =>
  state.location.selectedLocation;

export const getLocationList = (state: CommonRootState) =>
  state.location.locationList;

export const getSelectedLocation = createSelector(
  getRawSelectedLocationHash,
  getLocationList,
  getUserLocation,
  (selectedLocationHash, locationList, userLocation) => {
    if (selectedLocationHash) {
      const selectedLocation = locationList.find(
        (l) => l.hash === selectedLocationHash,
      );
      if (selectedLocation) {
        return selectedLocation;
      }
      if (selectedLocationHash === USER_LOCATION_HASH && userLocation) {
        return userLocation;
      }
    }
    if (userLocation) {
      return userLocation;
    }
    if (locationList.length > 0) {
      // If no location is selected, return the first location in the list
      return locationList[0];
    }
    return null;
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
    },
  },
);

const getFavoriteLocationHashs = (state: CommonRootState) =>
  state.location.favoriteLocationHashs;

export const getFavoriteLocations = createSelector(
  getLocationList,
  getFavoriteLocationHashs,
  (locationList, favoriteLocationHashs) => {
    // Create a map for quick location lookup
    const locationMap = new Map(
      locationList.map((location) => [location.hash, location]),
    );

    // Return locations in the same order as favoriteLocationHashs
    return favoriteLocationHashs
      .filter((hash) => hash !== USER_LOCATION_HASH)
      .map((hash) => locationMap.get(hash))
      .filter((location) => location !== undefined);
  },
);

export const getRecentLocations = createSelector(
  getLocationList,
  getFavoriteLocationHashs,
  (locationList, favoriteLocationHashs) => {
    return locationList.filter(
      (l) =>
        !favoriteLocationHashs.includes(l.hash) &&
        l.hash !== USER_LOCATION_HASH,
    );
  },
);

export const getHash = (_: CommonRootState, hash: string) => hash;

const getRawForecastModels = (state: CommonRootState) => {
  return state.location.forecastModels;
};

export const getForecastModelsByLocationHash = createSelector(
  getRawForecastModels,
  selectForecastModelOrder,
  getHash,
  (forecastModels, savedOrder, hash) => {
    if (hash in forecastModels) {
      const list = Forecast.ForecastUtils.getForecastModelList(
        forecastModels[hash],
      ).getList();
      return Forecast.ForecastUtils.orderForecastModelsByPreference(
        list,
        savedOrder,
      );
    }
    return [];
  },
);

const getRawSelectedForecastModel = (state: CommonRootState) => {
  return state.location.selectedForecastModel;
};

/** See the station twin — a frozen empty override dictionary. */
const NO_OVERRIDES: Record<string, string> = Object.freeze({});

export const getSelectedForecastModelByLocationHash = createSelector(
  getForecastModelsByLocationHash,
  getRawSelectedForecastModel,
  selectForecastModelOrder,
  getHash,
  selectIsPremium,
  resolveSelectedForecastModel,
);

/**
 * The model each place's forecast screen shows, as the server names it, by
 * place hash: `getSelectedForecastModelByLocationHash` for each, so the My
 * weather list's readings ask for what the screen they open shows (FE #381,
 * #378 rule 3). Null where none resolves yet (the place's model list not
 * fetched). Compare with `isEqual`: the per-hash calls share one memo.
 */
export const selectPlacesForecastModels = (
  state: CommonRootState,
  hashes: string[],
): Record<string, NormalizedForecastModel['serverId'] | null> =>
  Object.fromEntries(
    hashes.map((hash) => [
      hash,
      getSelectedForecastModelByLocationHash(state, hash)?.serverId ?? null,
    ]),
  );

/** See `getForecastSelectionByGenericStationId` — the location twin. */
export const getForecastSelectionByLocationHash = createSelector(
  getSelectedForecastModelByLocationHash,
  getForecastModelsByLocationHash,
  getRawSelectedForecastModel,
  getHash,
  selectForecastModelPins,
  selectForecastModelMode,
  selectForecastModelStrategy,
  selectIsPremium,
  selectForecastModelOrder,
  (resolved, list, overrides, hash, pins, mode, strategy, isPremium, order) =>
    deriveForecastSelection({
      resolved,
      // See the station twin: the same resolve with the overrides emptied.
      preferred: resolveSelectedForecastModel(
        list,
        NO_OVERRIDES,
        order,
        hash,
        isPremium,
      ),
      isOverridden: hash in overrides,
      pins,
      available: list.map((item) => item.serverId.forecastModel),
      hasPreference: Forecast.hasForecastPreference(pins, mode, strategy),
      // The same predicate the resolver two selectors up is fed. Anything else
      // lets the strip describe a model the resolver refuses to serve.
      isPremium,
    }),
);

/** Favourite places not on the account yet, for the sync (backend#266). */
export const selectUnsyncedFavoritePlaces = createSelector(
  getFavoriteLocations,
  (favorites) => favorites.filter((location) => !location.serverId),
);

/** Account places removed here and still to delete there (backend#266). */
export const selectDeletedServerIds = (state: CommonRootState) =>
  state.location.deletedServerIds;

/**
 * Favourite places whose forecast model list has not been fetched: what
 * `selectCoverageStations` counts as unknown on the location side.
 */
export const selectFavoritePlacesWithoutForecastModels = createSelector(
  getFavoriteLocations,
  getRawForecastModels,
  (favorites, forecastModels) =>
    favorites.filter((location) => !(location.hash in forecastModels)),
);
